import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useAuth } from '@toki/auth';
import {
  ArrowRight, CaretDown, ClockCounterClockwise, FilePdf, MagnifyingGlass, Plus, UploadSimple, X,
} from '@phosphor-icons/react';
import { Topbar } from '../components/Topbar';
import { abrirPdfEnPagina } from '../config/abrirPdf';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import {
  createPortalFaq,
  descargarPortalDocumento,
  editarPortalDocumento,
  eliminarPortalDocumento,
  getAsistenteConfig,
  listCategories,
  listPortalDocumentos,
  listPortalFaqs,
  probarAsistente,
  reindexarPortalFaqs,
  reprocesarPortalDocumento,
  restorePortalFaqVersion,
  savePortalFaqVersion,
  setAsistenteActivo,
  setPortalFaqActiva,
  subirPortalDocumento,
  updatePortalFaq,
  type AsistenteConfig,
  type AsistentePasoResultado,
  type AsistenteProbar,
  type DocumentoEstado,
  type DocumentoVisibilidad,
  type PortalDocumento,
  type PortalFaq,
} from '../services/api';
import styles from './AsistentePortal.module.css';

/**
 * ASISTENTE DEL PORTAL (pasos 92a y 92b): las preguntas frecuentes y los
 * documentos PDF con los que responde el chatbot del portal del cliente, y
 * «Probar».
 *
 * ⚠️ No es el «Asistente» del raíl (`Inbox.tsx`), que es la IA de staff.
 *
 * Maqueta: `docs/diseno/kivai-tickets-v6.html`, page `asistente`. Del diseño
 * NO se pinta (K29): las columnas «Respondidas · 30 d» y «Citado · 30 d» y la
 * caja «Preguntas sin respuesta esta semana» (92c, el uso). El interruptor
 * dice «Portal» y no «Portal y widget web»: el widget no se hace.
 *
 * Documentos (92b): un PDF nuevo nace «Solo agentes» —ningún cliente lo ve
 * hasta que alguien lo decida— y se procesa en segundo plano; la lista se
 * refresca sola mientras haya alguno «Procesando…». Un escaneado queda «Sin
 * texto legible»: no se hace OCR.
 *
 * Editan admin y coordinador (`asistente_portal:manage`); el técnico ve todo
 * en solo lectura y puede usar «Probar» (D1). Lo decide el backend
 * (`puedeEditar` en la lista), esto solo esconde los botones.
 *
 * La respuesta se cambia por VERSIONES: «Guardar como vN» y «Restaurar como
 * vN» crean una versión nueva; el historial no se reescribe.
 */

type Tab = 'faq' | 'docs' | 'probar';

// El resultado de cada escalón en la traza de «Probar» (los chips del v6).
const PASO_RES: Record<AsistentePasoResultado, { label: string; tono: string }> = {
  respondida: { label: 'Respondida', tono: 'ok' },
  sin_coincidencia: { label: 'Sin coincidencia', tono: 'aviso' },
  sin_servicio: { label: 'Sin servicio', tono: 'error' },
  no_hizo_falta: { label: 'No hizo falta', tono: 'skip' },
  ofrecido: { label: 'Ofrecido', tono: 'ticket' },
};
const PASO_TITULO = { faq: 'Preguntas frecuentes', documentos: 'Documentos', ticket: 'Abrir ticket' } as const;

const ESTADO_DOC: Record<DocumentoEstado, { label: string; tono: string }> = {
  listo: { label: 'Listo', tono: 'ok' },
  procesando: { label: 'Procesando…', tono: 'proc' },
  sin_texto: { label: 'Sin texto legible', tono: 'err' },
  error: { label: 'Error', tono: 'err' },
};
const VISIBLE: Record<DocumentoVisibilidad, string> = {
  clientes: 'Clientes y agentes',
  agentes: 'Solo agentes',
};

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

function pct(n: number | null): string {
  return n == null ? '—' : `${Math.round(n * 100)} %`;
}

function Interruptor({
  on, onChange, disabled, label,
}: { on: boolean; onChange: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={label}
      disabled={disabled}
      className={`${styles.switch} ${on ? styles.switchOn : ''}`}
      onClick={(e) => { e.stopPropagation(); onChange(); }}
    >
      <span className={styles.knob} />
    </button>
  );
}

export function AsistentePortalScreen() {
  const { provider } = useAuth();
  const mc = getModuleColor('asistentePortal');
  const ModuleIcon = getModuleIcon('asistentePortal');

  const [config, setConfig] = useState<AsistenteConfig | null>(null);
  const [faqs, setFaqs] = useState<PortalFaq[]>([]);
  const [docs, setDocs] = useState<PortalDocumento[]>([]);
  const [maxMb, setMaxMb] = useState(20);
  const [puedeEditar, setPuedeEditar] = useState(false);
  const [cats, setCats] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('faq');
  const [q, setQ] = useState('');
  const [abierta, setAbierta] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [probarCon, setProbarCon] = useState<string | null>(null);

  const token = useCallback(async () => {
    const t = await provider.getAccessToken();
    if (!t) throw new Error('Sesión caducada');
    return t;
  }, [provider]);

  const load = useCallback(async () => {
    try {
      const t = await token();
      const [cfg, lista, categorias, documentos] = await Promise.all([
        getAsistenteConfig(t), listPortalFaqs(t), listCategories(t).catch(() => []), listPortalDocumentos(t),
      ]);
      setConfig(cfg);
      setFaqs(lista.items);
      setDocs(documentos.items);
      setMaxMb(documentos.maxMb);
      setPuedeEditar(lista.puedeEditar);
      setCats(categorias);
      setAbierta((a) => a ?? lista.items[0]?.id ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const reemplazar = (f: PortalFaq) => setFaqs((xs) => xs.map((x) => (x.id === f.id ? f : x)));

  // Mientras haya un documento «Procesando…», la lista se pide cada 3 s. El
  // procesado es de segundos; si el servidor se reinicia a medias, el backend
  // lo pasa a «Error» a los 15 min y esto deja de preguntar.
  const procesando = docs.some((d) => d.estado === 'procesando');
  useEffect(() => {
    if (!procesando) return;
    const id = window.setInterval(async () => {
      try {
        setDocs((await listPortalDocumentos(await token())).items);
      } catch { /* el siguiente intento lo dirá */ }
    }, 3000);
    return () => window.clearInterval(id);
  }, [procesando, token]);

  const run = async (fn: (t: string) => Promise<void>) => {
    setError(null);
    try {
      await fn(await token());
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const toggleBot = () => config && run(async (t) => setConfig(await setAsistenteActivo(t, !config.activo)));
  const toggleFaq = (f: PortalFaq) => run(async (t) => reemplazar(await setPortalFaqActiva(t, f.id, !f.activa)));
  const reindexar = () => run(async (t) => {
    await reindexarPortalFaqs(t);
    setFaqs((await listPortalFaqs(t)).items);
    setDocs((await listPortalDocumentos(t)).items);
  });

  const filtradas = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return faqs;
    return faqs.filter((f) => [f.pregunta, ...f.variantes].join(' ').toLowerCase().includes(n));
  }, [faqs, q]);

  const sinIndexar = faqs.filter((f) => f.activa).reduce((s, f) => s + f.sinIndexar, 0);
  const trozosSinIndexar = docs.filter((d) => d.activo && d.estado === 'listo').reduce((s, d) => s + d.sinIndexar, 0);

  // Subir PDF: el botón de la cabecera y la zona de arrastre usan lo mismo.
  // Uno a uno, para que el error diga cuál; los que entran aparecen arriba
  // «Procesando…» y la lista se refresca sola.
  const elegirPdf = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(0);
  const subirPdfs = (ficheros: File[]) => {
    if (!ficheros.length) return;
    run(async (t) => {
      setSubiendo(ficheros.length);
      const errores: string[] = [];
      try {
        for (const f of ficheros) {
          try {
            const d = await subirPortalDocumento(t, f);
            setDocs((xs) => [d, ...xs]);
          } catch (e) {
            errores.push((e as Error).message);
          }
          setSubiendo((n) => n - 1);
        }
      } finally {
        setSubiendo(0);
      }
      if (errores.length) throw new Error(errores.join(' · '));
    });
  };

  const irAProbar = (texto: string) => {
    setProbarCon(texto);
    setTab('probar');
  };

  if (loading) return <div className={styles.loading}>Cargando…</div>;

  return (
    <div className={styles.page}>
      <Topbar
        title="Asistente del portal"
        subtitle="Las preguntas frecuentes y los documentos con los que responde el chatbot del portal del cliente"
        moduleIcon={<ModuleIcon weight="duotone" size={18} color={mc.accent} />}
        moduleColor={mc.accent}
        moduleBg={mc.bg}
      />

      <div className={styles.body}>
        {/* ── Cómo responde + interruptor ─────────────────── */}
        <section className={styles.como}>
          <div className={styles.comoLabel}>
            <span className={styles.caps}>Cómo responde</span>
            <span className={styles.comoSub}>En este orden, hasta encontrar respuesta</span>
          </div>
          <div className={styles.pasos}>
            <div className={`${styles.paso} ${styles.pasoPrimero}`}>
              <span className={styles.pasoNum}>1</span>
              <div className={styles.pasoTxt}>
                <div className={styles.pasoTitulo}>Preguntas frecuentes</div>
                <div className={styles.pasoSub}>Respuesta literal, revisada</div>
              </div>
            </div>
            <span className={styles.flecha} aria-hidden><ArrowRight size={18} weight="bold" /></span>
            <div className={`${styles.paso} ${styles.pasoMedio}`}>
              <span className={styles.pasoNum}>2</span>
              <div className={styles.pasoTxt}>
                <div className={styles.pasoTitulo}>Documentos</div>
                <div className={styles.pasoSub}>Redacta citando el PDF</div>
              </div>
            </div>
            <span className={styles.flecha} aria-hidden><ArrowRight size={18} weight="bold" /></span>
            <div className={`${styles.paso} ${styles.pasoUltimo}`}>
              <span className={styles.pasoNum}>3</span>
              <div className={styles.pasoTxt}>
                <div className={styles.pasoTitulo}>Abrir ticket</div>
                <div className={styles.pasoSub}>Si no encuentra nada</div>
              </div>
            </div>
          </div>
          <div className={styles.activo}>
            <div className={styles.activoTxt}>
              <span className={styles.activoTitulo}>Asistente activo</span>
              <span className={styles.activoSub}>Portal del cliente</span>
            </div>
            <Interruptor
              on={!!config?.activo}
              onChange={toggleBot}
              disabled={!puedeEditar}
              label={config?.activo ? 'Apagar el asistente del portal' : 'Encender el asistente del portal'}
            />
          </div>
        </section>

        {config && !config.embeddingsConfigurados && (
          <div className={styles.aviso} role="status">
            No hay modelo de embeddings configurado: el asistente no puede comparar preguntas.
            {config.activo ? ' Encendido, a cada pregunta solo ofrece abrir un ticket.' : ' Si lo enciendes, solo ofrecerá abrir un ticket.'}
          </div>
        )}
        {config && config.embeddingsConfigurados && !config.textoConfigurado && docs.length > 0 && (
          <div className={styles.aviso} role="status">
            No hay modelo de texto configurado: el asistente no puede redactar con los documentos. Si ninguna
            pregunta frecuente encaja, ofrecerá abrir un ticket.
          </div>
        )}
        {sinIndexar + trozosSinIndexar > 0 && (
          <div className={styles.aviso} role="status">
            <span>
              {sinIndexar > 0 && (
                <>
                  {sinIndexar === 1 ? '1 frase activa está' : `${sinIndexar} frases activas están`} sin indexar.{' '}
                </>
              )}
              {trozosSinIndexar > 0 && (
                <>
                  {trozosSinIndexar === 1 ? '1 trozo de documento está' : `${trozosSinIndexar} trozos de documentos están`}{' '}
                  sin indexar.{' '}
                </>
              )}
              No encajarán con ninguna pregunta hasta indexarlos.
            </span>
            {puedeEditar && <button type="button" className={styles.btnGhost} onClick={reindexar}>Reintentar</button>}
          </div>
        )}
        {error && <div className={styles.error} role="alert">{error}</div>}

        {/* ── Pestañas ────────────────────────────────────── */}
        <div className={styles.tabsRow}>
          <div className={styles.tabs} role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'faq'}
              className={`${styles.tab} ${tab === 'faq' ? styles.tabOn : ''}`} onClick={() => setTab('faq')}>
              Preguntas frecuentes<span className={styles.tabCnt}>{faqs.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={tab === 'docs'}
              className={`${styles.tab} ${tab === 'docs' ? styles.tabOn : ''}`} onClick={() => setTab('docs')}>
              Documentos<span className={styles.tabCnt}>{docs.length}</span>
            </button>
            <button type="button" role="tab" aria-selected={tab === 'probar'}
              className={`${styles.tab} ${tab === 'probar' ? styles.tabOn : ''}`} onClick={() => setTab('probar')}>
              Probar
            </button>
          </div>
          <div className={styles.spacer} />
          {tab === 'faq' && (
            <>
              <label className={styles.search}>
                <MagnifyingGlass size={15} weight="bold" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pregunta" aria-label="Buscar pregunta" />
              </label>
              {puedeEditar && (
                <button type="button" className={styles.btnPrimary} onClick={() => setCreando(true)}>
                  <Plus size={15} weight="bold" />Nueva pregunta
                </button>
              )}
            </>
          )}
          {tab === 'docs' && puedeEditar && (
            <button type="button" className={styles.btnPrimary} onClick={() => elegirPdf.current?.click()}>
              <UploadSimple size={15} weight="bold" />Subir PDF
            </button>
          )}
        </div>
        <input
          ref={elegirPdf}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => { subirPdfs(Array.from(e.target.files ?? [])); e.target.value = ''; }}
        />

        {tab === 'docs' && (
          <Documentos
            docs={docs}
            setDocs={setDocs}
            puedeEditar={puedeEditar}
            maxMb={maxMb}
            subiendo={subiendo}
            onSubir={subirPdfs}
            onElegir={() => elegirPdf.current?.click()}
            token={token}
            onError={setError}
          />
        )}
        {tab === 'faq' && (
          <div className={styles.tabla}>
            <div className={styles.thead}>
              <span>Pregunta</span><span>Categoría</span><span className={styles.right}>Activa</span>
            </div>
            {filtradas.map((f) => (
              <FilaFaq
                key={f.id}
                faq={f}
                cats={cats}
                abierta={abierta === f.id}
                puedeEditar={puedeEditar}
                onAbrir={() => setAbierta(abierta === f.id ? null : f.id)}
                onToggle={() => toggleFaq(f)}
                onCambio={reemplazar}
                onError={setError}
                onProbar={irAProbar}
                token={token}
              />
            ))}
            {filtradas.length === 0 && (
              <div className={styles.vacio}>
                {faqs.length === 0
                  ? 'Todavía no hay preguntas frecuentes. Sin ellas, el asistente ofrece abrir un ticket a todo.'
                  : `Ninguna pregunta coincide con «${q}».`}
              </div>
            )}
          </div>
        )}
        {tab === 'probar' && (
          <Probar faqs={faqs} token={token} inicial={probarCon} onUsado={() => setProbarCon(null)} />
        )}
      </div>

      {creando && (
        <NuevaFaq
          cats={cats}
          token={token}
          onCerrar={() => setCreando(false)}
          onCreada={(f) => { setFaqs((xs) => [f, ...xs]); setAbierta(f.id); setCreando(false); }}
        />
      )}
    </div>
  );
}

// ── Una fila de la tabla, con su panel ───────────────────────────────

function FilaFaq({
  faq, cats, abierta, puedeEditar, onAbrir, onToggle, onCambio, onError, onProbar, token,
}: {
  faq: PortalFaq;
  cats: { id: string; name: string }[];
  abierta: boolean;
  puedeEditar: boolean;
  onAbrir: () => void;
  onToggle: () => void;
  onCambio: (f: PortalFaq) => void;
  onError: (m: string | null) => void;
  onProbar: (texto: string) => void;
  token: () => Promise<string>;
}) {
  const vigente = faq.versiones.find((v) => v.n === faq.versionActual) ?? faq.versiones[0];
  const [texto, setTexto] = useState(vigente?.respuesta ?? '');
  const [nota, setNota] = useState('');
  const [verN, setVerN] = useState(faq.versionActual);
  const [nuevaVariante, setNuevaVariante] = useState('');
  const [guardando, setGuardando] = useState(false);
  const sig = Math.max(...faq.versiones.map((v) => v.n), 0) + 1;
  const vista = faq.versiones.find((v) => v.n === verN) ?? vigente;
  const viendoVieja = verN !== faq.versionActual;

  // Al cambiar la versión vigente (guardar o restaurar), el editor la recoge.
  useEffect(() => {
    setTexto(vigente?.respuesta ?? '');
    setVerN(faq.versionActual);
  }, [faq.versionActual, vigente?.respuesta]);

  const act = async (fn: (t: string) => Promise<PortalFaq>) => {
    setGuardando(true);
    onError(null);
    try {
      onCambio(await fn(await token()));
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  const guardarFrases = (variantes: string[], pregunta = faq.pregunta, categoryId = faq.categoryId) =>
    act((t) => updatePortalFaq(t, faq.id, { pregunta, categoryId, variantes }));

  const anadir = (e: FormEvent) => {
    e.preventDefault();
    const v = nuevaVariante.trim();
    if (!v) return;
    setNuevaVariante('');
    guardarFrases([...faq.variantes, v]);
  };

  const autor = faq.versiones[0];
  const formas = faq.variantes.length === 1 ? '1 forma más de preguntarlo' : `${faq.variantes.length} formas más de preguntarlo`;

  return (
    <div className={`${styles.fila} ${!faq.activa ? styles.filaInactiva : ''}`}>
      <div
        className={`${styles.filaHead} ${abierta ? styles.filaHeadOpen : ''}`}
        onClick={onAbrir}
        role="button"
        tabIndex={0}
        aria-expanded={abierta}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAbrir(); } }}
      >
        <div className={styles.filaPregunta}>
          <CaretDown size={14} weight="bold" className={`${styles.chev} ${abierta ? '' : styles.chevCerrado}`} />
          <div className={styles.minw}>
            <div className={styles.preguntaTxt}>{faq.pregunta}</div>
            <div className={styles.preguntaMeta}>
              v{faq.versionActual} · editada {fechaCorta(autor?.createdAt ?? faq.updatedAt)} por {autor?.autor ?? '—'} · {formas}
              {faq.sinIndexar > 0 && <span className={styles.sinIndexar}> · sin indexar</span>}
            </div>
          </div>
        </div>
        <span className={styles.categoria}>{faq.categoria ?? 'General'}</span>
        <div className={styles.right}>
          <Interruptor
            on={faq.activa}
            onChange={onToggle}
            disabled={!puedeEditar}
            label={faq.activa ? 'Desactivar: el asistente deja de usarla' : 'Activar: el asistente vuelve a usarla'}
          />
        </div>
      </div>

      {abierta && (
        <div className={styles.panel}>
          {/* ── Variantes ── */}
          <div className={styles.col}>
            <span className={styles.caps}>También se pregunta como</span>
            <div className={styles.chips}>
              {faq.variantes.map((v) => (
                <span key={v} className={styles.chip}>
                  {v}
                  {puedeEditar && (
                    <button type="button" className={styles.chipX} aria-label={`Quitar «${v}»`}
                      onClick={() => guardarFrases(faq.variantes.filter((x) => x !== v))} disabled={guardando}>
                      <X size={11} weight="bold" />
                    </button>
                  )}
                </span>
              ))}
              {faq.variantes.length === 0 && !puedeEditar && <span className={styles.muted}>Sin variantes.</span>}
            </div>
            {puedeEditar && (
              <form className={styles.anadir} onSubmit={anadir}>
                <input
                  value={nuevaVariante}
                  onChange={(e) => setNuevaVariante(e.target.value)}
                  placeholder="+ Añadir otra forma de preguntarlo"
                  aria-label="Añadir otra forma de preguntarlo"
                  maxLength={300}
                />
              </form>
            )}
            {puedeEditar && (
              <label className={styles.campoCat}>
                <span className={styles.caps}>Categoría</span>
                <select
                  value={faq.categoryId ?? ''}
                  onChange={(e) => guardarFrases(faq.variantes, faq.pregunta, e.target.value || null)}
                  disabled={guardando}
                >
                  <option value="">General</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
          </div>

          {/* ── Respuesta ── */}
          <div className={styles.col}>
            <span className={styles.caps}>Respuesta del asistente</span>
            {!viendoVieja ? (
              <>
                <textarea
                  className={styles.textarea}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  readOnly={!puedeEditar}
                  aria-label="Respuesta del asistente"
                />
                {puedeEditar && (
                  <input
                    className={styles.input}
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    placeholder="Qué has cambiado (se guarda en el historial)"
                    maxLength={200}
                  />
                )}
                <div className={styles.acciones}>
                  <button type="button" className={styles.btnGhost} onClick={() => onProbar(faq.variantes[0] ?? faq.pregunta)}>
                    Probar
                  </button>
                  {puedeEditar && (
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      disabled={guardando || !texto.trim() || texto.trim() === vigente?.respuesta}
                      onClick={() => act((t) => savePortalFaqVersion(t, faq.id, texto.trim(), nota.trim()))
                        .then(() => setNota(''))}
                    >
                      Guardar como v{sig}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.viendo}>
                  <ClockCounterClockwise size={15} weight="bold" />
                  <span>Viendo v{vista?.n} · {vista && fechaCorta(vista.createdAt)} · {vista?.autor}</span>
                </div>
                <div className={styles.textoViejo}>{vista?.respuesta}</div>
                <div className={styles.acciones}>
                  <button type="button" className={styles.btnGhost} onClick={() => setVerN(faq.versionActual)}>
                    Volver a la actual
                  </button>
                  {puedeEditar && vista && (
                    <button type="button" className={styles.btnPrimary} disabled={guardando}
                      onClick={() => act((t) => restorePortalFaqVersion(t, faq.id, vista.n))}>
                      Restaurar como v{sig}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ── Historial ── */}
          <div className={styles.col}>
            <span className={styles.caps}>Historial</span>
            <div className={styles.historial}>
              {faq.versiones.map((v) => (
                <button
                  key={v.n}
                  type="button"
                  className={`${styles.version} ${v.n === verN ? styles.versionSel : ''}`}
                  onClick={() => setVerN(v.n)}
                  aria-pressed={v.n === verN}
                >
                  <span className={styles.versionTop}>
                    <strong>v{v.n}</strong>
                    {v.n === faq.versionActual && <span className={styles.publicada}>Publicada</span>}
                    <span className={styles.versionFecha}>{fechaCorta(v.createdAt)}</span>
                  </span>
                  {v.nota && <span className={styles.versionNota}>{v.nota}</span>}
                  <span className={styles.versionAutor}>{v.autor}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Nueva pregunta ───────────────────────────────────────────────────

function NuevaFaq({
  cats, token, onCerrar, onCreada,
}: {
  cats: { id: string; name: string }[];
  token: () => Promise<string>;
  onCerrar: () => void;
  onCreada: (f: PortalFaq) => void;
}) {
  const [pregunta, setPregunta] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [variantes, setVariantes] = useState('');
  const [respuesta, setRespuesta] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      onCreada(await createPortalFaq(await token(), {
        pregunta: pregunta.trim(),
        categoryId: categoryId || null,
        variantes: variantes.split('\n').map((v) => v.trim()).filter(Boolean),
        respuesta: respuesta.trim(),
      }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className={styles.backdrop} onClick={onCerrar}>
      <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={enviar}
        role="dialog" aria-label="Nueva pregunta frecuente">
        <div className={styles.modalHead}>
          <h3>Nueva pregunta frecuente</h3>
          <button type="button" className={styles.modalX} onClick={onCerrar} aria-label="Cerrar"><X size={16} /></button>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        <label className={styles.campo}>
          <span>Pregunta</span>
          <input className={styles.input} value={pregunta} onChange={(e) => setPregunta(e.target.value)}
            placeholder="¿Cómo cambio mi contraseña?" required minLength={3} maxLength={300} autoFocus />
        </label>
        <label className={styles.campo}>
          <span>Categoría</span>
          <select className={styles.input} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">General</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className={styles.campo}>
          <span>Otras formas de preguntarlo <em>(una por línea)</em></span>
          <textarea className={styles.textarea} value={variantes} onChange={(e) => setVariantes(e.target.value)}
            placeholder={'Se me ha caducado la contraseña\nNo recuerdo mi clave'} rows={3} />
        </label>
        <label className={styles.campo}>
          <span>Respuesta <em>(el asistente la da tal cual, sin cambiar una palabra)</em></span>
          <textarea className={styles.textarea} value={respuesta} onChange={(e) => setRespuesta(e.target.value)}
            rows={5} required maxLength={4000} />
        </label>
        <div className={styles.acciones}>
          <button type="button" className={styles.btnGhost} onClick={onCerrar}>Cancelar</button>
          <button type="submit" className={styles.btnPrimary} disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar como v1'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Documentos (92b) ─────────────────────────────────────────────────

function Documentos({
  docs, setDocs, puedeEditar, maxMb, subiendo, onSubir, onElegir, token, onError,
}: {
  docs: PortalDocumento[];
  setDocs: (fn: (xs: PortalDocumento[]) => PortalDocumento[]) => void;
  puedeEditar: boolean;
  maxMb: number;
  subiendo: number;
  onSubir: (ficheros: File[]) => void;
  onElegir: () => void;
  token: () => Promise<string>;
  onError: (msg: string | null) => void;
}) {
  const [encima, setEncima] = useState(false);

  const reemplazar = (d: PortalDocumento) => setDocs((xs) => xs.map((x) => (x.id === d.id ? d : x)));
  const hacer = async (fn: (t: string) => Promise<void>) => {
    onError(null);
    try {
      await fn(await token());
    } catch (e) {
      onError((e as Error).message);
    }
  };

  const soltar = (e: DragEvent) => {
    e.preventDefault();
    setEncima(false);
    if (puedeEditar) onSubir(Array.from(e.dataTransfer.files));
  };
  const eliminar = (d: PortalDocumento) => {
    if (!window.confirm(`¿Eliminar «${d.nombre}»? Se borra el PDF y el asistente deja de usarlo. No se puede deshacer.`)) return;
    hacer(async (t) => {
      await eliminarPortalDocumento(t, d.id);
      setDocs((xs) => xs.filter((x) => x.id !== d.id));
    });
  };

  return (
    <>
      {puedeEditar && (
        <div
          className={`${styles.drop} ${encima ? styles.dropOn : ''}`}
          onDragOver={(e) => { e.preventDefault(); setEncima(true); }}
          onDragLeave={() => setEncima(false)}
          onDrop={soltar}
        >
          <span className={styles.dropIcon}><UploadSimple size={20} weight="bold" /></span>
          <div>
            <div className={styles.dropTitulo}>
              {subiendo > 0 ? `Subiendo ${subiendo === 1 ? '1 PDF' : `${subiendo} PDF`}…` : 'Arrastra PDF aquí'}
              {subiendo === 0 && (
                <> o <button type="button" className={styles.linkBtn} onClick={onElegir}>elige uno</button></>
              )}
            </div>
            <div className={styles.dropSub}>
              Hasta {maxMb} MB por archivo. Entran como «Solo agentes»: decide tú cuáles ven los clientes. El
              asistente solo usa los activos.
            </div>
          </div>
        </div>
      )}

      <div className={styles.tabla}>
        <div className={`${styles.thead} ${styles.docCols}`}>
          <span>Documento</span><span>Visible para</span><span>Estado</span><span className={styles.right}>Activo</span>
        </div>
        {docs.map((d) => {
          const e = ESTADO_DOC[d.estado];
          const meta = [
            d.paginas != null ? `${d.paginas} ${d.paginas === 1 ? 'página' : 'páginas'}` : null,
            `subido por ${d.autor}, ${fechaCorta(d.createdAt)}`,
          ].filter(Boolean).join(' · ');
          return (
            <div key={d.id} className={`${styles.docFila} ${styles.docCols} ${d.activo ? '' : styles.docInactivo}`}>
              <div className={styles.docNombre}>
                <span className={styles.pdfIcon} aria-hidden><FilePdf size={20} weight="duotone" /></span>
                <div className={styles.minw}>
                  <div className={styles.preguntaTxt} title={d.nombre}>{d.nombre}</div>
                  <div className={styles.preguntaMeta}>{meta}</div>
                  <div className={styles.docAcciones}>
                    <button type="button" className={styles.linkBtn}
                      onClick={() => hacer((t) => abrirPdfEnPagina(() => descargarPortalDocumento(t, d.id)))}>
                      Abrir
                    </button>
                    {/* También en «Listo»: releerlo entero tras un cambio del
                        procesado (la guía de Oracle se quedó en 400 páginas). */}
                    {puedeEditar && d.estado !== 'procesando' && (
                      <button type="button" className={styles.linkBtn}
                        onClick={() => hacer(async (t) => reemplazar(await reprocesarPortalDocumento(t, d.id)))}>
                        Volver a procesar
                      </button>
                    )}
                    {puedeEditar && d.estado !== 'procesando' && (
                      <button type="button" className={`${styles.linkBtn} ${styles.linkPeligro}`} onClick={() => eliminar(d)}>
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.minw}>
                {puedeEditar ? (
                  <select
                    className={styles.visibleSel}
                    value={d.visibilidad}
                    aria-label={`Quién ve «${d.nombre}»`}
                    onChange={(ev) => hacer(async (t) => reemplazar(
                      await editarPortalDocumento(t, d.id, { visibilidad: ev.target.value as DocumentoVisibilidad }),
                    ))}
                  >
                    <option value="clientes">{VISIBLE.clientes}</option>
                    <option value="agentes">{VISIBLE.agentes}</option>
                  </select>
                ) : (
                  <span className={styles.categoria}>{VISIBLE[d.visibilidad]}</span>
                )}
              </div>
              <div className={styles.docEstado}>
                <span className={`${styles.estado} ${styles[`estado_${e.tono}`]}`}>
                  <span className={styles.estadoDot} />{e.label}
                </span>
                {d.estado === 'procesando' && <div className={styles.barra}><div /></div>}
                {(d.estado === 'error' || d.estado === 'sin_texto') && d.error && (
                  <span className={styles.docNota}>{d.error}</span>
                )}
                {d.estado === 'listo' && d.sinIndexar > 0 && (
                  <span className={`${styles.docNota} ${styles.sinIndexar}`}>{d.sinIndexar} trozos sin indexar</span>
                )}
                {d.estado === 'listo' && !!d.paginasSinTexto && (
                  <span className={styles.docNota}>
                    {d.paginasSinTexto === 1 ? '1 página' : `${d.paginasSinTexto} páginas`} sin texto (no se leen)
                  </span>
                )}
              </div>
              <div className={styles.right}>
                <Interruptor
                  on={d.activo && d.estado === 'listo'}
                  disabled={!puedeEditar || d.estado !== 'listo'}
                  onChange={() => hacer(async (t) => reemplazar(await editarPortalDocumento(t, d.id, { activo: !d.activo })))}
                  label={d.activo ? `Dejar de usar «${d.nombre}» en el asistente` : `Usar «${d.nombre}» en el asistente`}
                />
              </div>
            </div>
          );
        })}
        {docs.length === 0 && (
          <div className={styles.vacio}>
            Todavía no hay documentos. Sin ellos, lo que no esté en las preguntas frecuentes acaba en un ticket.
          </div>
        )}
      </div>
    </>
  );
}

// ── Probar ───────────────────────────────────────────────────────────

function Probar({
  faqs, token, inicial, onUsado,
}: {
  faqs: PortalFaq[];
  token: () => Promise<string>;
  inicial: string | null;
  onUsado: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [preguntada, setPreguntada] = useState<string | null>(null);
  const [res, setRes] = useState<AsistenteProbar | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  const probar = useCallback(async (t: string) => {
    const limpio = t.trim();
    if (!limpio) return;
    setCargando(true);
    setError(null);
    setPreguntada(limpio);
    try {
      setRes(await probarAsistente(await token(), limpio));
    } catch (e) {
      setError((e as Error).message);
      setRes(null);
    } finally {
      setCargando(false);
    }
  }, [token]);

  useEffect(() => {
    if (inicial) {
      setTexto(inicial);
      probar(inicial);
      onUsado();
    }
  }, [inicial, probar, onUsado]);

  // Sugerencias: una variante de cada una de las dos primeras activas y una que
  // no está en ninguna, para ver también el camino del ticket.
  const opciones = useMemo(() => {
    const acts = faqs.filter((f) => f.activa).slice(0, 2).map((f) => f.variantes[0] ?? f.pregunta);
    return [...acts, '¿Me podéis instalar AutoCAD?'];
  }, [faqs]);

  return (
    <div className={styles.probar}>
      <section className={styles.cliente}>
        <div className={styles.clienteHead}>
          <div>
            <div className={styles.clienteTitulo}>Vista del cliente</div>
            <div className={styles.clienteSub}>Así responde en el portal</div>
          </div>
        </div>
        <div className={styles.conversacion}>
          {preguntada && <div className={styles.burbujaCliente}>{preguntada}</div>}
          {cargando && <div className={styles.burbujaBot}>…</div>}
          {!cargando && res && (
            <div className={styles.botCol}>
              <div className={styles.burbujaBot}>{res.respuesta}</div>
              {res.resultado === 'faq' && <span className={styles.fuente}>Pregunta frecuente · v{res.version}</span>}
              {res.fuente && (
                <button type="button" className={`${styles.fuente} ${styles.fuenteDoc}`}
                  title="Abrir el PDF en esa página"
                  onClick={() => {
                    const f = res.fuente!;
                    token().then((t) => abrirPdfEnPagina(() => descargarPortalDocumento(t, f.documentoId), f.pagina))
                      .catch((e) => setError((e as Error).message));
                  }}>
                  <FilePdf size={14} weight="duotone" />{res.fuente.nombre} · p. {res.fuente.pagina}
                </button>
              )}
              {res.ofrecerTicket && <span className={styles.ticketFalso}>Abrir ticket</span>}
              {/* D3 (Jose): bajo una respuesta redactada, el ticket siempre a mano. */}
              {res.resultado === 'documento' && <span className={styles.ticketSecundario}>¿No era esto? Abrir ticket</span>}
            </div>
          )}
          {!preguntada && <p className={styles.muted}>Escribe una pregunta como la escribiría un cliente.</p>}
        </div>
        <form className={styles.probarForm} onSubmit={(e) => { e.preventDefault(); probar(texto); }}>
          <input className={styles.input} value={texto} onChange={(e) => setTexto(e.target.value)}
            placeholder="Escribe una pregunta…" aria-label="Pregunta de prueba" maxLength={500} />
          <button type="submit" className={styles.btnPrimary} disabled={cargando || !texto.trim()}>Probar</button>
        </form>
        <div className={styles.opciones}>
          <span className={styles.muted}>Prueba con una de estas:</span>
          <div className={styles.chips}>
            {opciones.map((o) => (
              <button key={o} type="button" className={`${styles.opcion} ${o === preguntada ? styles.opcionOn : ''}`}
                onClick={() => { setTexto(o); probar(o); }}>
                {o}
              </button>
            ))}
          </div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </section>

      <section className={styles.traza}>
        <div className={styles.caps}>Qué ha hecho</div>
        {!res ? (
          <p className={styles.muted}>
            Aquí se ve con qué pregunta o con qué página de qué documento ha encajado, con qué puntuación y por qué
            ofrece ticket.
          </p>
        ) : (
          res.pasos.map((p, i) => {
            const pr = PASO_RES[p.resultado];
            const encendido = p.resultado === 'respondida' || p.resultado === 'ofrecido';
            return (
              <div key={p.escalon} className={styles.trazaPaso}>
                <span className={`${styles.trazaNum} ${encendido ? styles.trazaNumOn : ''}`}>{i + 1}</span>
                <div className={styles.minw}>
                  <div className={styles.trazaTop}>
                    <span className={styles.trazaTitulo}>{PASO_TITULO[p.escalon]}</span>
                    <span className={`${styles.res} ${styles[`res_${pr.tono}`]}`}>{pr.label}</span>
                  </div>
                  <div className={styles.trazaDet}>{p.detalle}</div>
                  {p.escalon === 'faq' && res.candidatas.length > 0 && (
                    <ul className={styles.candidatas}>
                      {res.candidatas.map((c) => (
                        <li key={c.faqId}>
                          <span className={c.puntuacion >= res.umbral ? styles.candOk : ''}>{pct(c.puntuacion)}</span>
                          <span className={styles.minw}>
                            {c.pregunta}
                            {c.frase !== c.pregunta && <span className={styles.muted}> · por «{c.frase}»</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {p.escalon === 'faq' && p.resultado !== 'sin_servicio' && (
                    <div className={styles.umbral}>Mínimo para responder: {pct(res.umbral)}</div>
                  )}
                  {p.escalon === 'faq' && res.sinIndexar > 0 && (
                    <div className={styles.umbral}>{res.sinIndexar} frase(s) activas sin indexar no se han comparado.</div>
                  )}
                  {p.escalon === 'documentos' && res.trozos.length > 0 && (
                    <ul className={styles.candidatas}>
                      {res.trozos.map((t, k) => (
                        <li key={`${t.documentoId}-${t.pagina}-${k}`}>
                          <span className={t.puntuacion >= res.umbralDoc ? styles.candOk : ''}>{pct(t.puntuacion)}</span>
                          <span className={styles.minw}>
                            {t.nombre} · p. {t.pagina}
                            <span className={styles.extracto}>{t.extracto}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {p.escalon === 'documentos' && p.resultado !== 'no_hizo_falta' && p.resultado !== 'sin_servicio' && (
                    <div className={styles.umbral}>
                      Mínimo para usar un documento: {pct(res.umbralDoc)}. Solo cuentan los activos, listos y
                      visibles para clientes.
                    </div>
                  )}
                  {p.escalon === 'documentos' && res.trozosSinIndexar > 0 && (
                    <div className={styles.umbral}>{res.trozosSinIndexar} trozo(s) sin indexar no se han comparado.</div>
                  )}
                  {p.escalon === 'ticket' && p.resultado === 'no_hizo_falta' && (
                    <div className={styles.trazaDet}>
                      {res.resultado === 'documento'
                        ? 'En el portal sale igualmente «¿No era esto? Abrir ticket» debajo de la respuesta.'
                        : 'Si el cliente dice que no le ha servido, se le ofrece igualmente.'}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
